import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Button, Calendar, Modal, Typography } from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';
import { TOrder } from 'types/Orders/order';

import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LocalizationProvider, MobileTimePicker } from '@mui/x-date-pickers';
import { ruRU } from '@mui/x-date-pickers/locales';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { getOrderLoading } from '@utils/redux/Loadings/selectors';

import { formatAndLocaleDate } from '../../common/helpers/formatAndLocaleDate';

import './orderDateChangeModal.scss';

type PickedType = 'start_planned_at' | 'finish_planned_at' | 'started_at' | 'finished_at';

interface Props {
    open: boolean;
    onCancel: () => void;
    onOk: (date: Date) => void;
    orderData: TOrder;
    type: PickedType;
    defaultDate?: Date;
}
type Operation = 'plus' | 'minus';

const PickedTypeToTitle = {
    start_planned_at: 'Запланированное время начала заказа',
    finish_planned_at: 'Запланированное время завершения заказа',
    started_at: 'Время начала заказа',
    finished_at: 'Время завершения заказа',
};

export const OrderDateChangeModal: React.FC<Props> = ({
    open,
    onCancel,
    onOk,
    orderData,
    type,
    defaultDate = null,
}) => {
    const [dateValue, setDateValue] = useState(null);
    const [earlierThanCurrentDate, setEarlierThanCurrentDate] = useState(false);
    const [openTimePicker, setOpenTimePicker] = useState(false);
    const [openDatePicker, setOpenDatePicker] = useState(false);
    const isOrderLoading = useSelector(getOrderLoading);

    const updateDate = (
        operation: Operation,
        amount: number,
        unit: moment.unitOfTime.DurationConstructor
    ) => {
        setDateValue((prev) => {
            const m = moment(prev);
            return operation === 'plus' ? m.add(amount, unit) : m.subtract(amount, unit);
        });
    };

    useEffect(() => {
        setDateValue(moment(orderData?.[type] || defaultDate));
    }, [orderData?.[type], defaultDate]);

    return (
        <Modal
            title={'Изменение даты'}
            open={open}
            onCancel={onCancel}
            centered
            footer={[
                <Button
                    type="primary"
                    key="confirm"
                    loading={isOrderLoading}
                    disabled={earlierThanCurrentDate}
                    onClick={() => onOk(dateValue.format('YYYY-MM-DD HH:mm:ss') as any)}
                >
                    Подтвердить
                </Button>,
            ]}
        >
            <div className="start-works-modal-container">
                <Typography.Title
                    className="start-works-modal-title"
                    style={{ textAlign: 'center', color: 'red' }}
                    level={5}
                >
                    {PickedTypeToTitle[type]}
                </Typography.Title>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: earlierThanCurrentDate ? 'red' : 'black',
                    }}
                >
                    <div style={{ padding: '7px', border: '1px solid #000', borderRadius: '25px' }}>
                        <Typography.Title
                            className="start-works-modal-real-start"
                            style={{ color: earlierThanCurrentDate ? 'red' : 'black' }}
                            level={3}
                            onClick={() => setOpenTimePicker(true)}
                        >
                            {moment(dateValue).format('HH:mm')}
                        </Typography.Title>
                    </div>
                    <Typography.Title
                        className="start-works-modal-real-start"
                        style={{
                            color: earlierThanCurrentDate ? 'red' : 'black',
                            padding: '0 10px',
                        }}
                        level={3}
                    >
                        /
                    </Typography.Title>
                    <div style={{ padding: '7px', border: '1px solid #000', borderRadius: '25px' }}>
                        <Typography.Title
                            className="start-works-modal-real-start"
                            style={{ color: earlierThanCurrentDate ? 'red' : 'black' }}
                            level={3}
                            onClick={() => setOpenDatePicker(true)}
                            data-testid="startWorks"
                        >
                            {formatAndLocaleDate(dateValue)}
                        </Typography.Title>
                    </div>
                </div>

                <div className="start-works-regulator-buttons">
                    {/* 10 min */}
                    <div className="start-works-regulator-button">
                        <Button
                            type="link"
                            icon={
                                <FontAwesomeIcon
                                    icon={faPlus}
                                    onClick={() => updateDate('plus', 10, 'minutes')}
                                />
                            }
                        />
                        <div className="start-works-regulator-button-text">
                            <Typography.Text>10</Typography.Text>
                            <Typography.Text>мин</Typography.Text>
                        </div>
                        <Button
                            type="link"
                            icon={
                                <FontAwesomeIcon
                                    icon={faMinus}
                                    onClick={() => updateDate('minus', 10, 'minutes')}
                                />
                            }
                        />
                    </div>

                    {/* 30 min */}
                    <div className="start-works-regulator-button">
                        <Button
                            type="link"
                            icon={
                                <FontAwesomeIcon
                                    icon={faPlus}
                                    onClick={() => updateDate('plus', 30, 'minutes')}
                                />
                            }
                        />
                        <div className="start-works-regulator-button-text">
                            <Typography.Text>30</Typography.Text>
                            <Typography.Text>мин</Typography.Text>
                        </div>
                        <Button
                            type="link"
                            icon={
                                <FontAwesomeIcon
                                    icon={faMinus}
                                    onClick={() => updateDate('minus', 30, 'minutes')}
                                />
                            }
                        />
                    </div>

                    {/* 1 hour */}
                    <div className="start-works-regulator-button">
                        <Button
                            type="link"
                            icon={
                                <FontAwesomeIcon
                                    icon={faPlus}
                                    onClick={() => updateDate('plus', 1, 'hours')}
                                />
                            }
                        />
                        <div className="start-works-regulator-button-text">
                            <Typography.Text>1</Typography.Text>
                            <Typography.Text>час</Typography.Text>
                        </div>
                        <Button
                            type="link"
                            icon={
                                <FontAwesomeIcon
                                    icon={faMinus}
                                    onClick={() => updateDate('minus', 1, 'hours')}
                                />
                            }
                        />
                    </div>

                    {/* 1 day */}
                    <div className="start-works-regulator-button">
                        <Button
                            type="link"
                            icon={
                                <FontAwesomeIcon
                                    icon={faPlus}
                                    onClick={() => updateDate('plus', 1, 'days')}
                                />
                            }
                        />
                        <div className="start-works-regulator-button-text">
                            <Typography.Text>1</Typography.Text>
                            <Typography.Text>день</Typography.Text>
                        </div>
                        <Button
                            type="link"
                            icon={
                                <FontAwesomeIcon
                                    icon={faMinus}
                                    onClick={() => updateDate('minus', 1, 'days')}
                                />
                            }
                        />
                    </div>
                </div>
            </div>

            <LocalizationProvider
                dateAdapter={AdapterMoment}
                localeText={ruRU.components.MuiLocalizationProvider.defaultProps.localeText}
            >
                <MobileTimePicker
                    ampm={false}
                    closeOnSelect={true}
                    value={dateValue}
                    onChange={(val) => setDateValue(val)}
                    open={openTimePicker}
                    onClose={() => setOpenTimePicker(false)}
                    slotProps={{
                        textField: { sx: { display: 'none' } },
                    }}
                />
            </LocalizationProvider>

            <Modal
                centered
                footer={[
                    <Button key="save" type="primary" onClick={() => setOpenDatePicker(false)}>
                        Сохранить
                    </Button>,
                ]}
                open={openDatePicker}
                onCancel={() => setOpenDatePicker(false)}
            >
                <Calendar
                    fullscreen={false}
                    value={dayjs(dateValue?.toISOString())}
                    onChange={(val) => setDateValue(moment(val.toISOString()))}
                />
            </Modal>
        </Modal>
    );
};
